import {isFeatureEnabled} from '@github-ui/feature-flags'

import {AuthToken} from './auth-token'
import {CopilotAuthTokenProvider} from './copilot-auth-token'

export const AGENT_SESSIONS_TOKEN_KEY = 'AGENT_SESSIONS_TOKEN'
export const CMC_AUTH_TOKEN_KEY = 'CMC_AUTH_TOKEN'

let agentSessionsTokenProviderInstance: AgentSessionsTokenProvider | null = null
let capiAgentSessionsTokenProviderInstance: AgentSessionsTokenProvider | null = null

/**
 * Returns a singleton instance of AgentSessionsTokenProvider.
 * This ensures request deduplication works across all callers.
 */
export function getAgentSessionsTokenProvider(pinnedToCapi = false): AgentSessionsTokenProvider {
  if (pinnedToCapi && isFeatureEnabled('copilot_mission_control_use_api_gateway')) {
    if (!capiAgentSessionsTokenProviderInstance) {
      capiAgentSessionsTokenProviderInstance = new AgentSessionsTokenProvider(true)
    }
    if (agentSessionsTokenProviderInstance) {
      capiAgentSessionsTokenProviderInstance.ssoOrgIDs = agentSessionsTokenProviderInstance.ssoOrgIDs
    }
    return capiAgentSessionsTokenProviderInstance
  }

  if (!agentSessionsTokenProviderInstance) {
    agentSessionsTokenProviderInstance = new AgentSessionsTokenProvider()
  }
  return agentSessionsTokenProviderInstance
}

/**
 * Resets the singleton instance. Only intended for use in tests.
 * @internal
 */
export function resetAgentSessionsTokenProviderForTests(): void {
  agentSessionsTokenProviderInstance = null
  capiAgentSessionsTokenProviderInstance = null
}

export class AgentSessionsTokenProvider extends CopilotAuthTokenProvider {
  protected readonly pinnedToCapi: boolean

  constructor(pinnedToCapi = false) {
    super([])
    this.pinnedToCapi = pinnedToCapi
  }

  protected override get tokenEndpoint(): string {
    return !this.pinnedToCapi && isFeatureEnabled('copilot_mission_control_use_api_gateway')
      ? '/copilot/agent-sessions/cmc-api-token'
      : '/copilot/agent-sessions/token'
  }

  protected override get storageKey(): string {
    return !this.pinnedToCapi && isFeatureEnabled('copilot_mission_control_use_api_gateway')
      ? CMC_AUTH_TOKEN_KEY
      : AGENT_SESSIONS_TOKEN_KEY
  }

  protected override async validateAuthToken(token: AuthToken, signal?: AbortSignal): Promise<AuthToken> {
    if (token.ssoChanged(this.ssoOrgIDs)) {
      return this.fetchAuthToken(false, signal)
    }

    if (!token.isExpired) {
      return token
    }

    return this.fetchAuthToken(false, signal)
  }

  /**
   * Attempts to refresh the token by forcing a server-side cache miss.
   * Use this after receiving a 401 (e.g., token was evicted).
   */
  async tryRefreshToken(): Promise<boolean> {
    if (this.isUnlicensed) {
      return false
    }

    try {
      await this.fetchAuthToken(true)
      return true
    } catch {
      return false
    }
  }
}
